import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Loader2 } from 'lucide-react';
import { Button } from 'src/components/ui/button';
import { useSendTestLink } from 'src/services/api/mutations/candidateMutations';
import { CandidateService } from 'src/services/api/CandidateService';
import { useToast } from 'src/hooks/use-toast';
import { cn } from 'src/lib/utils';
import { getToneButtonClasses } from 'src/lib/statusStyles';
import { registerAction } from './actionRegistry';

// 'slate' — a plain neutral utility action, same scheme senior_level_flow's
// own refresh_candidate_session uses — see actionColors.js.
const tone = getToneButtonClasses('slate');

// "Resend" — re-sends the test link email to a single candidate. Ported
// from the old RequirementPipeline.jsx's handleResendTestLink (Pending tab only,
// TA-only). The API needs a group_id, which isn't on the candidate row
// itself — it first looks up the candidate's test-score groups to get one,
// then calls the actual resend, exactly as the old handler did.
const ResendTestLinkAction = ({ candidateId, opportunityId, onSuccess }) => {
  const { toast } = useToast();
  const [isResending, setIsResending] = useState(false);
  const sendTestLinkMutation = useSendTestLink();

  const handleClick = async (e) => {
    e.stopPropagation();
    setIsResending(true);
    try {
      const scoresResponse = await CandidateService.getCandidateScores(
        candidateId,
        opportunityId
      );

      if (!scoresResponse?.data?.items?.groups?.length) {
        throw new Error('No test groups found for this candidate');
      }

      const [firstGroup] = scoresResponse.data.items.groups;

      const response = await sendTestLinkMutation.mutateAsync({
        candidate_reference_id: candidateId,
        requirement_id: parseInt(opportunityId, 10),
        group_id: firstGroup.id,
        order_number: 1,
      });

      if (response.success) {
        toast({
          message: response.message || 'Test link resent successfully',
          variant: 'success',
        });
        onSuccess(response);
      } else {
        toast({
          message: response.message || 'Failed to resend test link',
          variant: 'warning',
        });
      }
    } catch (error) {
      toast({
        message: error.message || 'Failed to resend test link',
        variant: 'destructive',
      });
    } finally {
      setIsResending(false);
    }
  };

  return (
    <Button
      variant="tone"
      size="sm"
      className={cn(
        'flex items-center gap-1 text-xs px-2.5',
        tone.bg,
        tone.text,
        tone.border,
        tone.hoverBg
      )}
      disabled={isResending}
      onClick={handleClick}
    >
      {isResending ? (
        <>
          <Loader2 className={cn('h-4 w-4 animate-spin', tone.text)} />
          Resending...
        </>
      ) : (
        <span>Resend</span>
      )}
    </Button>
  );
};

ResendTestLinkAction.propTypes = {
  candidateId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  opportunityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSuccess: PropTypes.func,
};

ResendTestLinkAction.defaultProps = {
  opportunityId: undefined,
  onSuccess: () => {},
};

registerAction({
  stateMachineKey: 'default',
  actionKey: 'resend_test_link',
  component: ResendTestLinkAction,
});

export default ResendTestLinkAction;
